import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import "@/App.css";
const AIChallengePage = lazy(() => import('@/pages/AIChallengePage'));
const MiniCoursePage = lazy(() => import('@/pages/MiniCoursePage'));
const MiniAdsPage = lazy(() => import('@/pages/MiniAdsPage'));
const AnalyticsPage = lazy(() => import('@/pages/AnalyticsPage'));
const SlabPage = lazy(() => import('@/pages/SlabPage'));
const VibeLabPage = lazy(() => import('@/pages/VibeLabPage'));
const DeconstructReconstructPage = lazy(() => import('@/pages/DeconstructReconstructPage'));
const BetaRebellionPage = lazy(() => import('@/pages/BetaRebellionPage'));
const BetaRebellionAdsPage = lazy(() => import('@/pages/BetaRebellionAdsPage'));
const BetaEnrollTrainingPage = lazy(() => import('@/pages/BetaEnrollTrainingPage'));
const BetaRebellionWaitlistPage = lazy(() => import('@/pages/BetaRebellionWaitlistPage'));
const SlabWaitlistPage = lazy(() => import('@/pages/SlabWaitlistPage'));
const AvatarPage = lazy(() => import('@/pages/AvatarPage'));
const AvatarWorkshopPage = lazy(() => import('@/pages/AvatarWorkshopPage'));
const AvatarReplayPage = lazy(() => import('@/pages/AvatarReplayPage'));
const CoursesPage = lazy(() => import('@/pages/CoursesPage'));
const AiToolsOptinPage = lazy(() => import('@/pages/AiToolsOptinPage'));
const AiToolsLibraryPage = lazy(() => import('@/pages/AiToolsLibraryPage'));
const LaunchMagnetPage = lazy(() => import('@/pages/LaunchMagnetPage'));
const KitPage = lazy(() => import('@/pages/KitPage'));
const TenKBetaPage = lazy(() => import('@/pages/TenKBetaPage'));
const TenKBetaAdsPage = lazy(() => import('@/pages/TenKBetaAdsPage'));
const TenKBetaAdsEgPage = lazy(() => import('@/pages/TenKBetaAdsEgPage'));
const TenKBetaEgPage = lazy(() => import('@/pages/TenKBetaEgPage'));
const TenKBetaThanksPage = lazy(() => import('@/pages/TenKBetaThanksPage'));
const TenKBetaThanksEgPage = lazy(() => import('@/pages/TenKBetaThanksEgPage'));
const BRCompletedOrderPage = lazy(() => import('@/pages/BRCompletedOrderPage'));
const BRSpecialPage = lazy(() => import('@/pages/BRSpecialPage'));
const BRSpecialEgPage = lazy(() => import('@/pages/BRSpecialEgPage'));
const TenKBetaReplayPage = lazy(() => import('@/pages/TenKBetaReplayPage'));
const TenKBetaReplayEgPage = lazy(() => import('@/pages/TenKBetaReplayEgPage'));
const SPRebellionPage = lazy(() => import('@/pages/SPRebellionPage'));
const AdminLogin = lazy(() => import('@/pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AuthCallback = lazy(() => import('@/pages/admin/AuthCallback'));
const ConciergeChatbot = lazy(() => import('@/components/ConciergeChatbot'));

const API = process.env.REACT_APP_BACKEND_URL;

const COMPONENTS = {
  MiniCoursePage,
  MiniAdsPage,
  SlabPage,
  VibeLabPage,
  DeconstructReconstructPage,
  BetaRebellionPage,
  BetaRebellionAdsPage,
  BetaEnrollTrainingPage,
  BetaRebellionWaitlistPage,
  SlabWaitlistPage,
  AvatarPage,
  AIChallengePage,
  CoursesPage,
  AiToolsOptinPage,
  AiToolsLibraryPage,
  LaunchMagnetPage,
  KitPage,
  TenKBetaPage,
  TenKBetaAdsPage,
  TenKBetaAdsEgPage,
  TenKBetaEgPage,
  TenKBetaThanksPage,
  TenKBetaThanksEgPage,
  BRCompletedOrderPage,
  BRSpecialPage,
  BRSpecialEgPage,
  TenKBetaReplayPage,
  TenKBetaReplayEgPage,
  SPRebellionPage,
};

function ExternalRedirect({ to }) {
  useEffect(() => {
    window.location.href = to;
  }, [to]);
  return null;
}

// Resolves any /:slug to the right page component (handles renamed slugs).
function DynamicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, component: null, redirect: null });

  useEffect(() => {
    if (!slug) return;
    fetch(`${API}/api/page/resolve?slug=${encodeURIComponent(slug)}`)
      .then((r) => r.json())
      .then((data) => {
        if (!data.enabled) {
          setState({ loading: false, component: null, redirect: null });
          return;
        }
        if (data.redirect_to && data.redirect_to !== slug) {
          navigate(`/${data.redirect_to}`, { replace: true });
          return;
        }
        setState({ loading: false, component: data.component, redirect: null });
      })
      .catch(() => setState({ loading: false, component: null, redirect: null }));
  }, [slug, navigate]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white/70">
        <p className="font-mono text-sm tracking-widest">LOADING…</p>
      </div>
    );
  }
  if (!state.component) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white px-6 text-center">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[#b94a32] font-bold mb-3">404</p>
        <h1 className="font-serif text-3xl mb-3">Page not found</h1>
        <p className="text-white/60 mb-6">No page exists at <code>/{slug}</code>.</p>
        <a href="https://secretaisociety.com" className="px-5 py-3 bg-[#b94a32] text-white text-xs uppercase tracking-[0.2em] font-bold">Go home</a>
      </div>
    );
  }
  const Comp = COMPONENTS[state.component];
  if (!Comp) return null;
  return <Comp />;
}

function PageFallback() {
  // Invisible fallback — chunk loads in <100ms, no flash
  return null;
}

function AppRouter() {
  const location = useLocation();

  // Fire Meta Pixel PageView on every SPA route change (the script in index.html only fires once on hard load)
  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname, location.search]);

  // Process OAuth callback fragment synchronously (before any other route renders)
  if (location.hash?.includes('session_id=')) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center bg-black text-white/70">
          <p className="font-mono text-sm tracking-widest">SIGNING YOU IN…</p>
        </div>
      }>
        <AuthCallback />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<ExternalRedirect to="https://secretaisociety.com" />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/avatarworkshop" element={<AvatarWorkshopPage />} />
        <Route path="/avatarreplay" element={<AvatarReplayPage />} />
        <Route path="/:slug" element={<DynamicPage />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
      <Suspense fallback={null}>
        <ConciergeChatbot />
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
